import React, { FC, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import axiosInstant from "../../../../../api";
import { deleteProductAdmin } from "../../../../../api/endpoints";
import { useUser } from "../../../../../hooks/useUser";
import { canDeleteProduct, productCannotDeleteMessage } from "../../../../../utils";
import { ConfirmationDialog } from "../../../../../components/ConfirmationDialog/ConfirmationDialog";
import {
  OverflowMenu,
  OverflowMenuGroup,
  MENU_ICONS as ICONS,
} from "../../../../../components/MenuOptions/OverflowMenu";
import { ConfirmDeleteModal } from "../../../productsV2/productListView/ConfirmDelete";
import type { ISingleProduct } from "../../../productsV2/productListView/constants";
import { isSessionRedirect } from "../pageModel";
import { useMenuTrigger } from "./useMenuTrigger";

/**
 * The header's ⋮: Settings, and "Delete product…" for `canDeleteProduct`
 * roles only (ADMIN, ACCOUNT_MANAGER_ADMIN, SALES_TEAM — utils/index.js:138),
 * the same gate as today's menu.
 *
 * `OverflowMenu` is used unedited. Its trigger is fixed at `w-7 h-[30px]`
 * (OverflowMenu.tsx:103) and it takes no class prop, so this wrapper sizes it:
 * 32px, 44px below md. The items stay 38px at every width — the menu renders
 * in a portal, out of the wrapper's reach (plan, "⋮ menus").
 *
 * Settings only asks the page to open the modal: the page owns it and
 * re-reads the header when it closes. The delete flow is a copy of
 * productsV2/productListView/ProductMenu.tsx:29-52,65-79 (confirm, 409 counts
 * dialog, the same toasts), then `onDelete` — the page's back to the list, as
 * today's header wired it (one push to the list, index.tsx `leave`). No `onUpdate`: productDetail/Header.tsx never
 * passed one to its menu, so the old page never called it.
 *
 * 196px wide, the handoff's menu width (README:68). The "Edit" group label
 * stays: `OverflowMenu` draws a label or a divider before every group
 * (OverflowMenu.tsx:127-131), so dropping it would put a rule at the top.
 * That label is also why the wrapper carries `useMenuTrigger`: MUI would
 * auto-focus it, and the menu couldn't be used from the keyboard.
 */

// The sizing wrapper. `[&>button]` outranks the trigger's own w-7/h-[30px]
// (class + element beats class); the menu itself is portalled, so it never matches.
const TRIGGER_SIZE = "inline-flex shrink-0 [&>button]:h-8 [&>button]:w-8 max-md:[&>button]:h-11 max-md:[&>button]:w-11";

interface Props {
  product: ISingleProduct;
  onSettings: () => void;
  /** After a successful delete: leave the page. */
  onDelete?: () => void;
}

export const ProductHeaderMenu: FC<Props> = ({ product, onSettings, onDelete }) => {
  const { role } = useUser();
  const canDelete = canDeleteProduct(role);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCannotDeleteDialog, setShowCannotDeleteDialog] = useState(false);
  const [cannotDeleteCounts, setCannotDeleteCounts] = useState({ projectCount: 0, videoCount: 0 });
  // One DELETE per confirm: the confirm button has no disabled state, and a
  // double click would otherwise send two.
  const deleting = useRef(false);
  const trigger = useMenuTrigger();

  const deleteProduct = () => {
    if (deleting.current) return;
    deleting.current = true;
    axiosInstant
      .delete(deleteProductAdmin(product.desireid))
      .then(() => {
        toast.success("Product deleted successfully!");
        setShowDeleteModal(false);
        onDelete?.();
      })
      .catch((error) => {
        setShowDeleteModal(false);
        // A session redirect (a 401, the expired-token cancel) is on its way
        // to login: no "deletion failed" toast on the way out.
        if (isSessionRedirect(error)) return;
        if (error.response?.status === 409) {
          const body = error.response.data || {};
          setCannotDeleteCounts({
            projectCount: body.projectCount || 0,
            videoCount: body.videoCount || 0,
          });
          setShowCannotDeleteDialog(true);
        } else {
          toast.error("Product deletion failed. Please try again.");
        }
      })
      .finally(() => {
        deleting.current = false;
      });
  };

  // Same groups as the product card's OverflowMenu (pages/products/ProductMenu.tsx:26-42):
  // an "Edit" group, then the destructive item below a divider.
  const groups = useMemo(
    (): OverflowMenuGroup[] => [
      {
        label: "Edit",
        items: [{ key: "settings", label: "Settings", icon: ICONS.settings, onClick: onSettings }],
      },
      {
        label: "",
        items: canDelete
          ? [
              {
                key: "delete",
                label: "Delete product…",
                icon: ICONS.trash,
                danger: true,
                onClick: () => setShowDeleteModal(true),
              },
            ]
          : [],
      },
    ],
    [canDelete, onSettings],
  );

  return (
    <>
      <div ref={trigger.ref} onClickCapture={trigger.onClickCapture} className={TRIGGER_SIZE}>
        <OverflowMenu groups={groups} ariaLabel="Product actions" width={196} />
      </div>
      <ConfirmDeleteModal
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onOkay={deleteProduct}
      />
      <ConfirmationDialog
        open={showCannotDeleteDialog}
        onClose={() => setShowCannotDeleteDialog(false)}
        onConfirm={() => setShowCannotDeleteDialog(false)}
        title="Delete Product"
        description={productCannotDeleteMessage(cannotDeleteCounts)}
        confirmLabel="Continue"
        cancelLabel=""
        showDivider={false}
      />
    </>
  );
};
